/**CORE LIBRARIES IMPORT */
import React, { useEffect, useRef, useState } from "react";

/**
 * SumUp Card Widget (LIVE mode only). Loads SumUp's script and mounts the
 * card form for a checkout the SERVER created. The widget's own response is
 * never trusted: `onFinished` makes the page ask the server to verify.
 * VERIFY the SDK URL and options against SumUp's current documentation, and
 * allow gateway.sumup.com in the Content Security Policy, before going live.
 */
const SDK_URL = "https://gateway.sumup.com/gateway/ecom/card/v2/sdk.js";
let sdkPromise = null;

function loadSdk() {
  if (window.SumUpCard) return Promise.resolve(window.SumUpCard);
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SDK_URL;
      s.async = true;
      s.onload = () => resolve(window.SumUpCard);
      s.onerror = () => {
        sdkPromise = null;
        reject(new Error("sumup_sdk_failed"));
      };
      document.head.appendChild(s);
    });
  }
  return sdkPromise;
}

const SumUpCardWidget = ({ checkoutId, onFinished }) => {
  const [failed, setFailed] = useState(false);
  const mounted = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadSdk()
      .then((SumUpCard) => {
        if (cancelled || !SumUpCard) return;
        mounted.current = SumUpCard.mount({
          id: "sumup-card",
          checkoutId,
          onResponse: (type) => {
            if (["success", "error", "fail"].includes(type)) onFinished();
          },
        });
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      try {
        mounted.current?.unmount?.();
      } catch {
        /* ignore */
      }
    };
  }, [checkoutId, onFinished]);

  return failed ? (
    <p className="checkout-error" role="alert">The secure payment form could not be loaded. Please refresh the page.</p>
  ) : (
    <div id="sumup-card" className="sumup-card" />
  );
};

export default SumUpCardWidget;
